/**
 * UserIdentity Value Object
 * Encapsulates user identity information and provides consistent formatting
 * across the application. Eliminates duplicate user display logic.
 */

import { AVATAR_COLORS } from '../constants';

interface UserIdentityData {
  owner_full_name?: string | null;
  owner_email?: string | null;
  owner_username?: string | null;
  owner_id?: string | null;
  user_id?: string | null;
  created_by?: string | null;
  full_name?: string | null;
  email?: string | null;
  username?: string | null;
}


/**
 * UserIdentity class encapsulates user identity information
 * and provides methods for consistent display formatting
 */
export class UserIdentity {
  private readonly fullName: string | null;
  private readonly email: string | null;
  private readonly username: string | null;
  private readonly userId: string | null;

  private constructor(
    fullName: string | null,
    email: string | null,
    username: string | null,
    userId: string | null
  ) {
    this.fullName = fullName;
    this.email = email;
    this.username = username;
    this.userId = userId;
  }

  /**
   * Create UserIdentity from a document or user object
   * Handles various field naming conventions
   */
  static fromDocument(data: UserIdentityData): UserIdentity {
    return new UserIdentity(
      data.owner_full_name || data.full_name || null,
      data.owner_email || data.email || null,
      data.owner_username || data.username || null,
      data.owner_id || data.user_id || data.created_by || null
    );
  }

  /**
   * Create UserIdentity from direct values
   */
  static create(
    fullName: string | null = null,
    email: string | null = null,
    username: string | null = null,
    userId: string | null = null
  ): UserIdentity {
    return new UserIdentity(fullName, email, username, userId);
  }

  /**
   * Get formatted display name for the user
   * Priority: fullName > email > username > userId
   */
  getDisplayName(): string {
    if (this.fullName) {
      return this.fullName;
    }

    if (this.email) {
      return this.formatEmailAsName(this.email);
    }

    if (this.username) {
      return this.formatUsername(this.username);
    }

    if (this.userId) {
      return this.formatUserId(this.userId);
    }

    return 'Unknown User';
  }

  /**
   * Get initials for avatar display (2 characters)
   */
  getInitials(): string {
    if (this.fullName) {
      return this.extractInitialsFromName(this.fullName);
    }

    if (this.email) {
      return this.extractInitialsFromEmail(this.email);
    }

    if (this.username) {
      return this.extractInitialsFromUsername(this.username);
    }

    if (this.userId) {
      return this.userId.slice(0, 2).toUpperCase();
    }

    return '?';
  }

  /**
   * Get consistent color for user avatar
   * Same user always gets same color
   */
  getAvatarColor(): string {
    const id = this.userId || this.email || this.username || 'default';
    const hash = this.hashString(id);
    return AVATAR_COLORS[hash % AVATAR_COLORS.length];
  }

  /**
   * Check if user identity has any data
   */
  isEmpty(): boolean {
    return !this.fullName && !this.email && !this.username && !this.userId;
  }

  /**
   * Get raw email value
   */
  getEmail(): string | null {
    return this.email;
  }

  /**
   * Get raw username value
   */
  getUsername(): string | null {
    return this.username;
  }

  /**
   * Get raw user ID value
   */
  getUserId(): string | null {
    return this.userId;
  }

  /**
   * Get tooltip text with full user information
   */
  getTooltipText(): string {
    const parts: string[] = [];

    if (this.fullName) {
      parts.push(this.fullName);
    }

    if (this.email) {
      parts.push(this.email);
    } else if (this.username) {
      parts.push(this.username);
    }

    if (parts.length === 0 && this.userId) {
      parts.push(`User ID: ${this.userId}`);
    }

    return parts.join(' • ') || 'Unknown User';
  }

  // Private formatting methods

  /**
   * Format email address as a readable name
   * Example: john.doe@example.com -> John Doe
   */
  private formatEmailAsName(email: string): string {
    if (!email.includes('@')) {
      return email;
    }

    const username = email.split('@')[0];
    return username
      .replace(/[._-]/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .split(/\s+/)
      .map(word => this.capitalize(word))
      .join(' ');
  }

  /**
   * Format username as readable text
   * Example: john_doe -> John Doe
   */
  private formatUsername(username: string): string {
    if (username.includes('@')) {
      return this.formatEmailAsName(username);
    }

    return username
      .replace(/[._-]/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .split(/\s+/)
      .map(word => this.capitalize(word))
      .join(' ');
  }

  /**
   * Format user ID for display
   * Shows last 6 chars for MongoDB ObjectIds
   */
  private formatUserId(userId: string): string {
    if (this.isMongoObjectId(userId)) {
      return `User ${userId.slice(-6)}`;
    }
    return this.formatUsername(userId);
  }

  /**
   * Extract initials from full name
   * Example: John Michael Doe -> JD
   */
  private extractInitialsFromName(name: string): string {
    const parts = name.trim().split(/\s+/);

    if (parts.length >= 2) {
      // Use first and last name initials
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }

    // Single name - use first 2 characters
    return name.slice(0, 2).toUpperCase();
  }

  /**
   * Extract initials from email address
   * Example: john.doe@example.com -> JD
   */
  private extractInitialsFromEmail(email: string): string {
    if (!email.includes('@')) {
      return email.slice(0, 2).toUpperCase();
    }

    const username = email.split('@')[0];
    const parts = username.split(/[._-]/);

    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }

    return username.slice(0, 2).toUpperCase();
  }

  /**
   * Extract initials from username
   */
  private extractInitialsFromUsername(username: string): string {
    return this.extractInitialsFromEmail(username);
  }

  /**
   * Check if string is a MongoDB ObjectId (24 hex characters)
   */
  private isMongoObjectId(id: string): boolean {
    return /^[0-9a-f]{24}$/i.test(id);
  }

  /**
   * Capitalize first letter of a word
   */
  private capitalize(word: string): string {
    if (!word) {return word;}
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }

  /**
   * Generate consistent hash code for a string
   * Used for color selection
   */
  private hashString(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
  }
}

/**
 * Type guard to check if an object has user identity fields
 */
export function hasUserIdentity(obj: unknown): obj is UserIdentityData {
  if (!obj || typeof obj !== 'object') {
    return false;
  }

  const data = obj as Record<string, unknown>;
  return Boolean(data.owner_full_name ||
    data.owner_email ||
    data.owner_username ||
    data.owner_id ||
    data.user_id ||
    data.created_by ||
    data.full_name ||
    data.email ||
    data.username);
}
